import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Card, Tag, Tooltip } from "@arco-design/web-react";
import { IconLink } from "@arco-design/web-react/icon";
import classNames from "classnames";

import { ReactComponent as LocationIcon } from "../../assets/icons/location.svg";
import { ReactComponent as LinkedinIcon } from "../../assets/icons/linkedin.svg";
import { ProfilePreviewState, User } from "../../models/Person";
import { UserAvatar } from "../UserAvatar/UserAvatar";
import { fixUrl } from "../../utils/fixUrl";
import { getButtonColor, getButtonTextColor, getButtonDisabledColor } from "../../utils/cssVariables";
import { useTranslations } from "../../utils/hooks/useTranslations";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";

import './UserCard.css';

type Props = {
  user: User;
  hideActions?: boolean;
  /** Hide "Book meeting" (e.g. admin users). Social link icons still show when present. */
  hideBooking?: boolean;
  withShadow?: boolean;
  /** When set (e.g. on admin User Profile), show ADMIN, MENTOR, MENTEE, or GUEST tag above name */
  roleTag?: 'ADMIN' | 'MENTOR' | 'MENTEE' | 'GUEST';
  /** Secondary line under name (e.g. admin email on users list) */
  secondaryLine?: string;
  /** Meta line under secondary (e.g. "Admin · Active") */
  metaLine?: string;
  /** When set, whole card is clickable and navigates to this URL (public mentor/mentee profile) */
  toProfileUrl?: string;
  profilePreview?: ProfilePreviewState;
};

export function UserCard({
  user,
  hideActions,
  hideBooking,
  withShadow,
  roleTag,
  secondaryLine,
  metaLine,
  toProfileUrl,
  profilePreview,
}: Props) {
  const { t } = useTranslation();
  const { translateSelected } = useTranslations();
  const isCalendlyLinkRequired = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);
  const navigate = useNavigate();

  const handleLinkOpen = (e: Event, url?: string) => {
    e.preventDefault();
    if (toProfileUrl) e.stopPropagation();
    if (url) {
      window.open(fixUrl(url), "_blank");
    }
  };

  const handleCardClick = () => {
    if (toProfileUrl) {
      navigate(toProfileUrl, profilePreview ? { state: { profilePreview } } : undefined);
    }
  };

  const handleBookingClick = (e: Event) => {
    e.preventDefault();
    if (toProfileUrl) e.stopPropagation();
    isCalendlyLinkRequired ? window.open(user.calendlyLink, '_blank') :
      navigate(`${routes.ADMIN_USER_BOOKING.replace(":id", user.id.toString())}`);
  };

  const actionButtons = !hideActions && (
    <div className="user-card__action-buttons" onClick={(e) => e.stopPropagation()}>
      {!hideBooking && (
      <Tooltip
        className='profile-booking-link-tooltip'
        content={t('common.no_calendly_link_message')}
        disabled={!isCalendlyLinkRequired || !!user.calendlyLink}
      >
        <Button
          style={{
            color: getButtonTextColor(),
            backgroundColor: isCalendlyLinkRequired && !user.calendlyLink ? getButtonDisabledColor() : getButtonColor(),
          }}
          disabled={isCalendlyLinkRequired && !user.calendlyLink}
          size="mini"
          shape={"round"}
          onClick={handleBookingClick}
        >
          {isCalendlyLinkRequired ? t('common.book_via_calendly') : t('common.book_meeting')}
        </Button>
      </Tooltip>
      )}
      <div className="user-card__action-buttons__icons">
        {user.website && (
          <Button
            className="user-card__icon-button user-card__icon-button--primary"
            size="mini"
            shape={"round"}
            onClick={(e) => handleLinkOpen(e, user.website)}
          >
            <IconLink fontSize={18}/>
          </Button>
        )}
        {user.linkedIn && (
          <Button
            className="user-card__icon-button"
            size="mini"
            shape={"round"}
            onClick={(e) => handleLinkOpen(e, user.linkedIn)}
          >
            <LinkedinIcon/>
          </Button>
        )}
      </div>
    </div>
  );

  const cardContent = (
    <>
      <UserAvatar
        avatarUrl={user.avatar_url}
        userId={user.id}
        firstName={user.name}
        lastName={user.last_name}
        size={64}
        shape="circle"
        alt={[user.name, user.last_name].filter(Boolean).join(' ')}
        className="user-card__avatar"
      />
      <div className="user-card__card-info">
        <div className="user-card__card-info__general-info">
          {roleTag && <div className="user-card__role-tag">{roleTag}</div>}
          <div className="user-card__name">{user.name} {user.last_name}</div>
          {secondaryLine && <div className="user-card__secondary-line">{secondaryLine}</div>}
          {metaLine && <div className="user-card__meta-line">{metaLine}</div>}
          <div className="user-card__info-row">
            {user.shortDescription && <div className="user-card__professional-title">{user.shortDescription}</div>}
            {user.location && !useNycIntake && <div className="user-card__location"><LocationIcon/>{user.location}</div>}
          </div>
        </div>
        <div className="user-card_categories">
          {user.categories && translateSelected('categories', user.categories).map((category: string) => (
            <Tag className="user-card__category-tag" size="small" key={category}>{category}</Tag>
          ))}
        </div>
        {actionButtons}
      </div>
    </>
  );

  return (
    <Card
      className={classNames('user-card', {
        'user-card__with-shadow': withShadow,
        'user-card--with-role-tag': roleTag,
        'user-card--clickable': !!toProfileUrl,
      })}
      bordered={false}
      hoverable
      onClick={toProfileUrl ? handleCardClick : undefined}
    >
      {cardContent}
    </Card>
  );
}
